
/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
User Context
********************************************************************************************
Listens to changes to authentication, user and pushes update to server

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       21st December 2020

*******************************************************************************************/
import React                    from 'react';
import md5                      from 'md5';
import { useAuth }              from 'contexts/AuthContext';
import { useAPIHealth  }        from 'contexts/APIHealthContext';
import { useAlert }             from 'contexts/AlertContext';
import { useJWT }               from 'hooks/services';
import { useCancelToken }       from 'hooks';
import { useAxios }             from 'hooks/services'
import { FaRegObjectUngroup }   from 'react-icons/fa';
import config                   from '../config';

const {
    // rootUrl,
    auth0 : {
        /*clientId,
        domain,
        scope,
        audience,
        nativeCallbackUri,
        */
        namespace   : AUTH0_NAMESPACE,
    }
} = config;

// The User Context 
const UserContext = React.createContext(undefined);

// The Polling Frequency
const POLL_FREQUENCY = 5000;

const DEFAULT_STATE = {
    verified                    : false, 
    emailConfirmationLastSent   : undefined, 
    metadata                    : {}, 
    geolocation                 : {
        countryCode : undefined,
        stateCode   : undefined,
        city        : undefined,
        lat         : undefined, 
        lng         : undefined
    },
    roles                       : [], 
    isAdmin                     : false, 
    isSuspended                 : false,
    isSocial                    : undefined,
    emailMD5                    : undefined
};

// Voltage Provider
const UserProvider = ({children}) => {
    const {axios}                               = useAxios();
    const {alert}                               = useAlert();
    const {isAPIHealthy}                        = useAPIHealth();
    const { 
        error,
        isAuthenticated,
        isLoading,
        isLoaded,
        user,
        loginWithRedirect,
        loginWithPopup,
        logout
    }                                           = useAuth();
    const { token, expires, ready }             = useJWT();
    const { cancelToken }                       = useCancelToken();
    const [userId,          setUserId]          = React.useState(undefined);
    const [updatingServer,  setUpdatingServer]  = React.useState(false);
    const [blocked,         setBlocked]         = React.useState(false);
    const [data,            setData]            = React.useState(undefined);
    const isUserCurrent                         = React.useMemo(() => Boolean(data), [data])

    React.useEffect(() => {
        if(error)
            console.error(error);
    },[error])

    /*
    const logout                                = React.useCallback( (options) => {
        const parms = isNative
            ? {
                logoutParams : {
                    returnTo    : '/' || rootUrl, 
                    ...options?.logoutParams
                },
                openUrl: async (url) => {
                    try {
                      await Browser.open({ url });
                    } catch (err) {
                      console.error('Failed to open browser during logout redirect:', err);
                      // Optional: Fallback, alert, or custom error UI
                    }
                  },
            }
            : {
                logoutParams : {
                    returnTo    : rootUrl, 
                    ...options?.logoutParams
                }
            }
        auth0Logout({
            clientId,
            ...options,
            ...parms
        })
    }, [auth0Logout, isNative]);
    */

    const logoutWithDelay                       = React.useCallback( (delay) => (options) => setTimeout(() => logout(options), delay), [logout]);

    /*
    const exchangeCodeForToken = React.useCallback( async (code)  => {
        const tokenUrl          = `https://${domain}/oauth/token`;
        const finalScope        = scope.includes('offline_access') ? scope : `${scope} offline_access`;

        const payload       = new URLSearchParams({
            grant_type   : 'authorization_code',
            client_id    : clientId,
            redirect_uri : nativeCallbackUri,
            code_verifier: codeVerifier,
            code         : code,
            audience     : audience,
            scope        : finalScope
        });
      
        let response;
        try {
            response = await fetch(tokenUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: payload.toString() // important: convert to url-encoded string
            });
        
            const data = await response.json();
        
            if (!response.ok) {
                throw new Error(data.error_description || 'Token exchange failed');
            }
        
            console.log('Token response:');
            console.log(JSON.stringify(data,null,4));
        
            // Store tokens (you may want to handle refresh tokens securely too)
            localStorage.setItem('access_token', data.access_token);
            localStorage.setItem('id_token', data.id_token);
            localStorage.setItem('expires_in', data.expires_in);
            localStorage.setItem('refresh_token', data.refresh_token); // Store refresh token

            const accessToken   = await getAccessTokenSilently({cacheMode: "off", detailedResponse : true});
            console.log(accessToken);
        
            return data;
          
        } catch (error) {
          console.error('Failed to exchange code for token:');
          console.log(error);
          try {
            const text = await response.text();
            console.error('Response body:', text);
          } catch (_) {
            console.error('No response body');
          }
          throw error;
        }
    }, [getAccessTokenSilently]);
    */

    /*
    const loginWithRedirect = React.useCallback(async () => {
        setBlocked(false);  // Reset any UI state
      
        console.log("Login With Redirect Called");
      
        // Generate a CSRF-safe state
        const state = Math.random().toString(36).substring(2, 15);
        localStorage.setItem('auth_state', state);
      
        console.log(`Setting State: ${state}`);

        const finalScope    = scope.includes('offline_access') ? scope : `${scope} offline_access`;

        const codeChallenge = await generateCodeChallenge(codeVerifier);
      
        // Build Auth URL
        const authUrl = `https://${domain}/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(nativeCallbackUri)}&response_type=code&prompt=login&scope=${encodeURIComponent(finalScope)}&audience=${encodeURIComponent(audience)}&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
      
        // Setup listener cleanup stub
        let removeListener = () => {};
      
        // Handle redirect from Auth0 back to app
        const handleRedirect = async ({ url }) => {
      
          if (url?.startsWith(nativeCallbackUri)) {
            const parsed        = new URL(url);
            const code          = parsed.searchParams.get('code');
            const returnedState = parsed.searchParams.get('state');
            const storedState   = localStorage.getItem('auth_state');
      
            if (returnedState !== storedState) {
              console.error('State mismatch - potential CSRF');
              return;
            }
      
            if (code) {
              console.log('Authorization code:', code);
              await Browser.close();
      
              // TODO: Exchange the code for a token
              // await exchangeCodeForToken(code);
              try {
                await exchangeCodeForToken(code);
                console.log('🎉 User is logged in!');
              } catch (err) {
                console.error('Token exchange error:', err);
              }
      
              // Clean up listener
              removeListener();
            }
          }
        };
      
        // Open the browser and add listener
        Browser.open({ url: authUrl }).catch((err) => {
          console.error('Failed to open browser:', err);
        });
      
        CapApp.addListener('appUrlOpen', handleRedirect).then(listener => {
          removeListener = () => listener.remove();
        });
      
    }, [exchangeCodeForToken]);
    */

    // Force Prompt
    /*
    const loginWithRedirectOld                   = React.useCallback((options = {}) => {
        setBlocked(false);
        
        // Generate a CSRF-safe state
        const state = encodeURIComponent(Math.random().toString(36).substring(2, 15));
        localStorage.setItem('auth_state', state);

        // Prepare login options
        const loginOptions = {
            ...options, 
            appState: { 
                returnTo    : `${location.pathname}${location.search}${location.hash}`, // https://community.auth0.com/t/redirect-after-login-page/63011
                ...options?.appState
            },
            authorizationParams: {
                prompt      : 'login',                  // Forces login, not cached session
                ...options?.authorizationParams,        // Allow additional options to override the default ones
                state       : state,
            },
            state
        };
        if(isNative){
            return loginWithRedirectBase({
                ...loginOptions,
                openUrl: async (url) => {
                    try {
                      await Browser.open({ url });
                    } catch (err) {
                      console.error('Failed to open browser during login redirect:', err);
                      // Optional: Fallback, alert, or custom error UI
                    }
                },
            });
            
        }
        
        return loginWithRedirectBase(loginOptions)

    }, [isNative, location.hash, location.pathname, location.search, loginWithRedirectBase])
    */

    // Force Prompt
    /*
    const loginWithPopup                        = React.useCallback((options = {}, config = {}) => {
        setBlocked(false);

        console.log("Login With Popup Called");

        // Prepare login options
        const loginOptions = {
            ...options,
            authorizationParams : {
                prompt      : 'login',              // Forces login, not cached session
                ...options?.authorizationParams     // Allow additional options to override the default ones
            }
        };

        // For native apps, use loginWithRedirect directly
        if (isNative) {
            // Trigger the redirect-based login flow directly for native environments
            return loginWithRedirect(loginOptions);
        }

        return loginWithPopupBase(loginOptions, config);  // Use the popup for web-based environments
        
    }, [isNative, loginWithPopupBase, loginWithRedirect])
    */
    
    // State
    const [state, setState] = React.useState(DEFAULT_STATE);

    const isReadyToPoll = React.useMemo(() => (
        Boolean(isAuthenticated && user && token && ready && isAPIHealthy)
    ), [isAPIHealthy, isAuthenticated, ready, token, user]);

    // Destroy Data if Not Authenticated
    React.useEffect(() => {
        if(!isAuthenticated)
            setData(undefined);
    },[isAuthenticated])

    // Function to send email confirmation request
    const sendEmailResetRequest = React.useCallback( () => new Promise((resolve,reject)=>{
        if(isAuthenticated && isAPIHealthy){
            axios.post('/api/auth/management/sendPasswordReset',{},{cancelToken})
                .then(response => {
                    resolve(response)
                })
                .catch(FaRegObjectUngroup)
        }else{
            reject(new Error('User is not authenticated'));
        }
    }),[axios, cancelToken, isAPIHealthy, isAuthenticated ])

    // Function to resend email confirmation
    const resendEmailConfirmation = React.useCallback( () => new Promise((resolve,reject)=>{
        if(isAuthenticated && isAPIHealthy){
            setState(prev => ({...prev, emailConfirmationLastSent : Date.now()})) // Assume it proceeds
            axios.post('/api/auth/management/sendEmailVerification', {}, {cancelToken})
                .then(({data}) => {
                    resolve(data);
                })
                .catch(err => {
                    setState(prev => ({...prev, emailConfirmationLastSent : undefined}))
                    reject(err);
                });
        }else{
            reject(new Error('User is not authenticated'));
        }
    }),[axios, cancelToken, isAPIHealthy, isAuthenticated])

    // Check Verified
    React.useEffect(() => {
        setState(prev => ({
            ...prev, 
            verified : Boolean(isAuthenticated && (user || {}).email_verified)
        }));
    },[isAuthenticated, user])

    // TO MD5
    React.useEffect(() => {
        const toMD5 = email => md5((email || '').trim().toLowerCase())
        const emailMD5 = isAuthenticated 
            ? toMD5((user || {}).email) 
            : undefined;
        setState(prev => ({...prev, emailMD5}));
    },[isAuthenticated, user])

    // Funciton to update user
    const updateUser = React.useCallback( () => new Promise((resolve,reject) => {
        const {nickname, email, email_verified, family_name = nickname, given_name = nickname, sub: openid, picture} = user;
        setBlocked(false);
        setUpdatingServer(true);
        axios.post( '/api/auth/update', { openid, email, email_verified, family_name, given_name, picture } )
            .then(({data}) => {
                const { blocked = false, deleted = false} = data;
                setData(data);
                setBlocked(blocked || deleted);
                resolve(true);
            })
            .catch(err => {
                setData(undefined);
                alert(err.message,'error');
                setTimeout(logout, 3000);
                reject(err)
            })
            .finally(() => {
                setUpdatingServer(false);
            })

    }),[alert, axios, logout, user])

    // Update user
    React.useEffect(() => {
        if(isReadyToPoll){
            updateUser().catch(console.error);
        }
    },[updateUser, isReadyToPoll])

    // Poll for user update if ready, and not currently updating
    const intervalRef = React.useRef(null);
    React.useEffect(() => {
        clearInterval(intervalRef.current);
        if(!isReadyToPoll || isUserCurrent || updatingServer)
            return;
        intervalRef.current = setInterval( async () => {
            try {
                await updateUser();    
            } catch (err) {
                console.error(err);
            }
        }, POLL_FREQUENCY);
        return () => {
            clearInterval(intervalRef.current);
        }
    },[isReadyToPoll, updateUser, isUserCurrent, updatingServer])

    // If user is current, post to logged in
    React.useEffect(() => {
        if(isReadyToPoll && isUserCurrent && isAPIHealthy){
            setBlocked(false);
            axios.post('/api/auth/login', {}, {cancelToken})
                .then(({user : { _id, blocked = false, deleted = false}}) => {
                    setUserId(_id);
                    setBlocked(blocked || deleted);
                })
                .catch(err => {
                    if(err?.isCancel) 
                        return;
                    alert(err.message, 'error');
                    setTimeout(logout, 3000);
                    setUserId(undefined);
                });

            return () => {
                setUserId(undefined);
            }
        }
    },[alert, axios, cancelToken, isAPIHealthy, isReadyToPoll, isUserCurrent, logout])

    // If user is not authenticated, post to logout
    React.useEffect(() => {
        if(!isAuthenticated && isAPIHealthy){
            axios.post('/api/auth/logout', {}, { cancelToken })
                .catch(console.error);
        }
    },[axios, isAuthenticated, isAPIHealthy, cancelToken])

    // Logout if blocked
    const timeoutRef = React.useRef(null);
    React.useEffect(() => {

        clearTimeout(timeoutRef.current);

        if(!isAuthenticated || !blocked)
            return;

        alert("User Blocked, Logginhg Out", 'error');

        timeoutRef.current = setTimeout(()=> {
            logout();
        }, 2500);

        return () => {
            clearTimeout(timeoutRef.current);
        }
        
    },[alert, blocked, isAuthenticated, logout])

    // Update 
    React.useEffect(() => {

        // Extract the roles
        const metadata      = isAuthenticated ? ((user || {})[`${AUTH0_NAMESPACE}/metadata`] || {}) : {};
        const roles         = isAuthenticated ? (metadata?.roles || []) : []
        const isAdmin       = roles.includes('admin');
        const isSuspended   = roles.includes('suspended');

        // Check if user is a social user or not
        const openid        = (user || {}).sub;
        const isSocial      = typeof openid === 'string' 
                ? !openid.startsWith('auth0') 
                : false;

        setState(prev => ({
            ...prev,
            metadata,
            roles,
            isAdmin,
            isSuspended,
            isSocial
        }))

    },[isAuthenticated, user])

    // Resolve geolocation from metadata
    React.useEffect(() => {

        if(isAuthenticated && ready && state.metadata){

            // Extract the geo data from user auth metadata
            let {geoip = {}} = state.metadata || {};

            // Function to clear the geoip data
            const eraseGeoIP = () => (
                setState(prev => ({
                    ...prev,
                    geolocation : {
                        countryCode     : undefined, 
                        stateCode       : undefined, 
                        city            : undefined, 
                        lat             : undefined, 
                        lng             : undefined
                    }
                }))
            );

            // if not an object, then destroy and return 
            if(typeof geoip !== 'object') {
                return eraseGeoIP();
            }

            // Deconstruct
            let countryCode = geoip?.country_code   || geoip?.countryCode,
                city        = geoip?.city_name      || geoip?.cityName || geoip?.city,
                stateCode   = geoip?.state_code     || geoip?.stateCode || geoip?.subdivisionCode, // Not provided by google, not sure about facebook etc..
                lat         = geoip?.latitude       || geoip?.lat,
                lng         = geoip?.longitude      || geoip?.lng || geoip?.lon


            // Get cities for country
            if(!stateCode && countryCode && city){

                axios.get(`/api/user/address/lookup/citiesOfCountry/${countryCode}`, {cancelToken})
                    .then(({data = []}) => data)
                    .then(cities => {
                        if(Array.isArray(cities) && cities.length){
                            let stateCodes = cities.filter(item => item.name === city);
                            if(stateCodes.length === 1)
                                stateCode = stateCodes[0].stateCode;
                            // Update the state
                            setState(prev => ({
                                ...prev,
                                geolocation : { countryCode, stateCode, city, lat, lng }
                            }));
                        }
                    })
                    .catch(err => {
                        //
                    })
                    
            }else{
                // Update the state
                setState(prev => ({
                    ...prev,
                    geolocation : {countryCode, stateCode, city, lat, lng}
                }));
            }

            // Cleanup
            return ()=>{
                eraseGeoIP();
            }
        }
    },[axios, cancelToken, isAuthenticated, ready, state.metadata])

    /*
    React.useEffect(() => {
        if(isAuthenticated && user?.blocked)
            logout();
    },[isAuthenticated, logout, user?.blocked])
    */

    const canPurchase = React.useMemo(() => (
        [
            isAuthenticated,  
            state?.verified, 
            ! (state?.isSuspended || false)
        ].every(Boolean)
    ), [isAuthenticated, state.isSuspended, state.verified])

    // Context values
    const value = {
        error,
        ready,
        isLoading,
        isLoaded,
        user,
        userId,
        token,
        expires,
        isUserCurrent,
        canPurchase,
        updatingServer,
        isAuthenticated             : isUserCurrent && isAuthenticated && ready,
        isValid                     : isUserCurrent && isAuthenticated && ready && !state.isSuspended,
        verified                    : state.verified,
        metadata                    : state.metadata,
        geolocation                 : state.geolocation,
        roles                       : state.roles,
        isAdmin                     : state.isAdmin,
        isSuspended                 : state.isSuspended,
        isSocial                    : state.isSocial,
        emailConfirmationLastSent   : state.emailConfirmationLastSent,
        emailMD5                    : state.emailMD5,
        data,
        sendEmailResetRequest,
        resendEmailConfirmation,
        loginWithRedirect,
        loginWithPopup,
        logout,
        logoutWithDelay
    };

    // console.log(value)

    /*
    React.useEffect(() => {
        console.log({isUserCurrent,isAuthenticated,ready})
    },[isUserCurrent,isAuthenticated,ready])
    */

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    )
}

// Voltage Consumer
const UserConsumer = ({children}) => {
    return (
        <UserContext.Consumer>
            {(context) => {
                if (context === undefined) {
                    throw new Error('UserConsumer must be used within UserProvider');
                }
                return children(context)
            }}
        </UserContext.Consumer>
    )
}

// useVoltage Hook
const useUser = () => {
    const context = React.useContext(UserContext);
    if(context === undefined)
        throw new Error('useUser must be used within UserProvider');
    return context;
}

// HOC to wrap components in UserProvider
const withUser = (Component) => (props) => (
    <UserProvider>
        <Component {...props} />
    </UserProvider>
);

export {
    UserProvider,
    UserConsumer,
    useUser,
    withUser
}
